/**
 * VCL Step-6 customer control-plane types (C1-C10).
 * Horizontal semantic IDs are shared; activation is per-surface (`surfaces.ts`).
 */

export const VCL_CONTROL_IDS = [
  'C1',
  'C2',
  'C3',
  'C4',
  'C5',
  'C6',
  'C7',
  'C8',
  'C9',
  'C10',
] as const;

export type VclControlId = (typeof VCL_CONTROL_IDS)[number];

/** Integer step relative to frozen live baseline (`0` = zero-delta). */
export type VclControlState = Readonly<Record<VclControlId, number>>;

export type SurfaceId = 'customer' | 'admin' | 'kiosk' | 'pickup';

export type VclSurfaceActivation = 'active' | 'inactive';

export type VclContentWidthRole =
  | 'shell'
  | 'standard'
  | 'wide'
  | 'narrow'
  | 'auth'
  | 'authFieldStack';

/** C1 roles - must match customer.extensions.css --vcl-type-{role}-* set (G3). */
export const VCL_TYPE_ROLES = [
  'body',
  'body-sm',
  'label',
  'button',
  'h3',
  'h4',
] as const;

export type VclTypeRole = (typeof VCL_TYPE_ROLES)[number];

/** Mutable working copy used by apply/clamp helpers. */
export type MutableVclControlState = Record<VclControlId, number>;

export interface VclClampNote {
  readonly controlId: VclControlId;
  readonly reason: string;
  readonly requested: number;
  readonly effective: number;
}

export interface VclEffectiveResult {
  readonly state: VclControlState;
  readonly clamps: readonly VclClampNote[];
  /**
   * Post-clamp CSS custom properties for `state` (incl. C8 content-max rem floors).
   * Same map Export puts on `effectiveCssVars` — single SOT with `buildVclCssVars`.
   */
  readonly cssVars: Readonly<Record<string, string>>;
}

export interface VclPromotionDelta {
  readonly controlId: VclControlId;
  readonly promotionTarget: string;
  readonly requestedStep: number;
  readonly effectiveStep: number;
  readonly baselineStep: number;
  readonly cssVars: Readonly<Record<string, string>>;
}

export interface VclExportPayload {
  readonly surface: SurfaceId;
  readonly exportedAt: string;
  readonly note: string;
  readonly deltas: readonly VclPromotionDelta[];
  readonly effectiveState: VclControlState;
  /** Full post-clamp `--vcl-*` map (always includes C8 content-max rem floors). */
  readonly effectiveCssVars: Readonly<Record<string, string>>;
  readonly clamps: readonly VclClampNote[];
}
